// ── Board card multi-select (TPT9099) ──
//
// The selection gestures of the board's two card views — kanban.js (one group per status column)
// and sprint-board.js (one group per sprint lane, or a single flat grid with sprints off) — over
// the shared selection model of components/bulk-actions.js. board.js owns that selection and the
// bulk-action toolbar: it re-mounts these views on every reload, and the selection has to outlive
// a re-mount so one bulk action can follow another. A view draws the checkboxes with the builders
// below and binds the gestures with bindCardSelection():
//
//   - a hover checkbox beside each card's key; a click toggles that card
//   - Cmd/Ctrl-click on a card toggles it
//   - Shift-click on a card or its checkbox selects (or clears) the cards from the last one clicked
//     to this one, within one group only
//   - a tri-state "select all" box in each group header
//   - Escape clears the selection, unless something else owns that Escape
//
// Ticks follow the selection in place (sync()), never through a re-render, so a hover-expanded
// card, an open picker or the drawer's board focus survive a toggle. See tt-web-kanban.md § Card
// multi-select.

import { escapeHtml } from '../utils.js';

// escapeHtml() escapes & < > but not '"' — needed for values written into attributes. String()
// first: escapeHtml() reads a falsy value as empty, and a sprint group id can be 0 (the backlog).
function escapeAttr(value) {
  return escapeHtml(String(value ?? '')).replace(/"/g, '&quot;');
}

const CHECK_STATES = new Set(['true', 'false', 'mixed']);

// One glyph for both checked states: the stylesheet shows the tick for aria-checked="true" and
// the dash for "mixed" (a group partly selected).
const BOX_ICON = '<svg class="card-select-icon" viewBox="0 0 16 16" aria-hidden="true" focusable="false">'
  + '<path class="card-select-tick" d="M3.5 8.5l3 3 6-6.5"/><path class="card-select-dash" d="M4.5 8h7"/></svg>';

// The checkbox beside a card's key. A <button role="checkbox">, not an <input>: its own click
// carries the modifier keys (a label-wrapped input gets a re-dispatched synthetic click), and both
// card drags already ignore a press that lands on a button.
export function cardCheckboxHtml(taskKey, checked = false) {
  const key = escapeAttr(taskKey);
  return `<button type="button" class="card-select" role="checkbox" aria-checked="${checked ? 'true' : 'false'}"`
    + ` aria-label="Select ${key}" title="Select (Shift-click selects a range)" data-select-key="${key}">${BOX_ICON}</button>`;
}

// The "select all" box in a group header; `state` comes from groupCheckState().
export function groupCheckboxHtml(groupId, label, state = 'false') {
  const name = escapeAttr(label);
  const checked = CHECK_STATES.has(state) ? state : 'false';
  return `<button type="button" class="card-select card-select--group" role="checkbox" aria-checked="${checked}"`
    + ` aria-label="Select all in ${name}" title="Select all in ${name}" data-select-group="${escapeAttr(groupId)}">${BOX_ICON}</button>`;
}

// 'true' when every key is selected, 'mixed' when some are, 'false' when none are (or there are
// no keys).
export function groupCheckState(keys, selection) {
  const list = keys || [];
  let picked = 0;
  for (const key of list) if (selection && selection.has(key)) picked += 1;
  if (picked === 0) return 'false';
  return picked === list.length ? 'true' : 'mixed';
}

// The keys from `fromKey` to `toKey`, both included, in `orderedKeys` order whichever way round
// they sit; null when either is missing (the anchor is in another group, or gone).
export function rangeKeys(orderedKeys, fromKey, toKey) {
  const from = orderedKeys.indexOf(fromKey);
  const to = orderedKeys.indexOf(toKey);
  if (from < 0 || to < 0) return null;
  return orderedKeys.slice(Math.min(from, to), Math.max(from, to) + 1);
}

// Open UI that handles Escape itself, or should be closed before the selection is: a modal, the
// assignee picker, the sprint board's status dropdown, a filter-bar dropdown, and the member-combo
// and tag-typeahead dropdowns (the bulk toolbar's among them).
const OWNS_ESCAPE = '.modal-overlay, .assignee-dropdown, .sb-status-dropdown, .ws-dropdown.open, .member-combo-dropdown, .tag-typeahead-dropdown';

// Whether a keydown clears the selection: a bare Escape nothing else has handled, pressed outside
// any text field, while no drawer, modal or dropdown is open — the first Escape closes those.
export function escapeClearsSelection(e, doc = document) {
  if (!e || e.key !== 'Escape' || e.defaultPrevented || e.isComposing) return false;
  if (e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return false;
  const target = e.target;
  if (target && typeof target.closest === 'function' && target.closest('input, textarea, select, [contenteditable]')) return false;
  if (doc.body && doc.body.classList.contains('task-detail-open')) return false;
  return !doc.querySelector(OWNS_ESCAPE);
}

// bindCardSelection(board, opts) — wires the gestures on one card view's board element.
//
// opts:
//   selection          createBulkSelection()  required (board.js's)
//   groupBodySelector  string                 the element holding one group's cards; a Shift-click
//                                             range never leaves it
//   groupKeys(id)      -> string[]            every task key of the group a header box names, read
//                                             from the view's data (a collapsed lane counts too)
//
// Cards are `.task-card[data-task-key]` holding a cardCheckboxHtml() box; header boxes come from
// groupCheckboxHtml(). Returns { gesture(e, card), sync(), unbind() }: a view calls gesture() at
// the top of its card mousedown handler — true means the press was a selection gesture
// (Cmd/Ctrl/Shift) and must not start a drag, open a picker or open the drawer — and sync() after
// each repaint.
export function bindCardSelection(board, { selection, groupBodySelector, groupKeys = () => [] } = {}) {
  if (!board || !selection) return { gesture: () => false, sync() {}, unbind() {} };
  let anchor = null; // key of the last card picked: where a Shift-click range starts

  function sync() {
    board.classList.toggle('board-selecting', selection.size > 0);
    board.querySelectorAll('.task-card[data-task-key]').forEach((card) => {
      const on = selection.has(card.dataset.taskKey);
      card.classList.toggle('card-bulk-selected', on);
      const box = card.querySelector('.card-select[data-select-key]');
      if (box) box.setAttribute('aria-checked', String(on));
    });
    board.querySelectorAll('.card-select[data-select-group]').forEach((box) => {
      box.setAttribute('aria-checked', groupCheckState(groupKeys(box.dataset.selectGroup), selection));
    });
  }

  function rangeTo(card, key) {
    const body = groupBodySelector ? card.closest(groupBodySelector) : null;
    if (!body || anchor == null || anchor === key) return null;
    const order = [...body.querySelectorAll('.task-card[data-task-key]')].map(el => el.dataset.taskKey);
    return rangeKeys(order, anchor, key);
  }

  // Flips the card — or, for a range, gives every card from the anchor to it the card's new state
  // (Gmail's rule: Shift-click an unticked card ticks the run, a ticked one unticks it).
  function pick(card, range) {
    const key = card.dataset.taskKey;
    const on = !selection.has(key);
    const keys = range ? rangeTo(card, key) : null;
    if (!keys) selection.toggle(key, on);
    else if (on) selection.addAll(keys);
    else selection.deleteAll(keys);
    anchor = key;
  }

  function gesture(e, card) {
    if (!card || e.button !== 0 || !(e.metaKey || e.ctrlKey || e.shiftKey)) return false;
    e.preventDefault(); // no text selection, no focus change
    pick(card, e.shiftKey);
    return true;
  }

  function onClick(e) {
    const box = e.target.closest('.card-select');
    if (!box || !board.contains(box)) return;
    const groupId = box.dataset.selectGroup;
    if (groupId !== undefined) {
      const keys = groupKeys(groupId);
      if (groupCheckState(keys, selection) === 'true') selection.deleteAll(keys);
      else selection.addAll(keys);
      return;
    }
    const card = box.closest('.task-card[data-task-key]');
    if (card) pick(card, e.shiftKey);
  }

  function onKeyDown(e) {
    if (selection.size > 0 && escapeClearsSelection(e)) selection.clear();
  }

  board.addEventListener('click', onClick);
  // Capture phase: decided before a bubble-phase Escape listener (the drawer's, a modal's) closes
  // its own UI and makes the same keypress look free.
  document.addEventListener('keydown', onKeyDown, true);
  const unsubscribe = selection.subscribe(sync);
  sync();

  return {
    gesture,
    sync,
    unbind() {
      board.removeEventListener('click', onClick);
      document.removeEventListener('keydown', onKeyDown, true);
      unsubscribe();
    },
  };
}
